import { useMemo, useState } from "react";
import Sidebar from "components/Overlays/ResponsiveSheet";
import { FiCheck, FiPlus, FiTag } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useAddTags from "api/TagResource/useAddTags";
import { TAG_PRESETS, parseTagNames } from "./tagPresets";
import TagDraftPreview from "./TagDraftPreview";
import "components/v2/FormField/formFields.css";
import "./tags.css";

export default function AddTags({ existingTags = [] }) {
	const [display, setDisplay] = useState(false);
	const [text, setText] = useState("");
	const [error, setError] = useState("");
	const { mutateAsync, isLoading } = useAddTags();
	const existing = useMemo(() => new Set(existingTags.map((tag) => tag.name?.toLowerCase())), [existingTags]);
	const parsed = useMemo(() => parseTagNames(text), [text]);
	const duplicates = parsed.filter((name) => name.length <= 60 && existing.has(name.toLowerCase()));
	const tooLong = parsed.filter((name) => name.length > 60);
	const fresh = parsed.filter((name) => name.length <= 60 && !existing.has(name.toLowerCase()));
	const changeText = (value) => { setText(value); setError(""); };
	const close = () => { if (isLoading) return; setDisplay(false); setText(""); setError(""); };
	const handleSubmit = async (event) => {
		event.preventDefault();
		if (isLoading || !fresh.length || tooLong.length) return;
		setError("");
		try {
			const result = await mutateAsync(fresh);
			if (result?.failed?.length) {
				setText(result.failed.map(({ name }) => name).join("\n"));
				setError(`${result.created} ${result.created === 1 ? "tag was" : "tags were"} added. The remaining ${result.failed.length === 1 ? "tag couldn’t" : "tags couldn’t"} be added. Review the names below and try again.`);
			} else { setDisplay(false); setText(""); }
		} catch { setError("Your tags couldn’t be added. Your draft is saved here — check the names and try again."); }
	};
	const parsedLower = new Set(parsed.map((name) => name.toLowerCase()));
	return <>
		<button type="button" onClick={() => setDisplay(true)} className="portal-button portal-button-primary tag-add-button"><FiPlus aria-hidden="true" />Add tags</button>
		<Sidebar position="right" visible={display} onHide={close} className="tag-create-sheet" header="Add book tags" icon={<FiTag />}
			description="Create one tag or add a whole set in a single step." showCloseIcon={!isLoading} closeOnEscape={!isLoading} dismissable={!isLoading}>
			<form onSubmit={handleSubmit} className="tag-create-form" aria-busy={isLoading}>
				<div className="akello-field"><label htmlFor="tag-names" className="akello-field-label">Tag names</label>
					<textarea id="tag-names" value={text} onChange={(event) => changeText(event.target.value)} rows={4} disabled={isLoading}
						placeholder={"e.g. Mathematics, Grade 7, Revision guide"} className="akello-field-control" aria-describedby="tag-names-help" />
					<p id="tag-names-help" className="akello-field-help">Separate names with commas or new lines. Up to 60 characters per tag.</p>
				</div>
				<section className="tag-bundles" aria-label="Starter bundles"><h2>Start with a bundle</h2><p>Add a ready-made set, then remove any tags you don’t need.</p>
					<div>{TAG_PRESETS.map((preset) => {
						const added = preset.tags.every((name) => parsedLower.has(name.toLowerCase()));
						return <button type="button" key={preset.id} disabled={isLoading} aria-pressed={added} onClick={() => changeText(parseTagNames([text, ...preset.tags].join("\n")).join("\n"))}>
							{added ? <FiCheck aria-hidden="true" /> : <FiPlus aria-hidden="true" />}<span>{preset.label}</span><span>{preset.tags.length}</span></button>;
					})}</div>
				</section>
				{error && <p role="alert" className="akello-form-error">{error}</p>}
				<TagDraftPreview names={parsed} duplicates={duplicates} tooLong={tooLong} disabled={isLoading} onRemove={(name) => changeText(parsed.filter((value) => value !== name).join("\n"))} />
				<div className="akello-form-actions is-sticky tag-create-actions"><button type="button" className="akello-action" disabled={isLoading} onClick={close}>Cancel</button>
					<button type="submit" className="akello-action akello-action-primary" disabled={isLoading || fresh.length === 0 || tooLong.length > 0}>
						{isLoading && <AiOutlineLoading3Quarters className="animate-spin" aria-hidden="true" />}{isLoading ? "Creating tags…" : fresh.length > 1 ? `Create ${fresh.length} tags` : "Create tag"}</button></div>
			</form>
		</Sidebar>
	</>;
}
